/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class ContentInfoImpl
 * @hideconstructor
 * @classdesc Widget Service Implementation class
 */
define([
	'../../../dashboard/dashboardServiceability/nls/StringResources'
], (
	StringResources
) => {
	const GENERAL_INFO = 'generalInfo';
	class ContentInfoImpl {
		constructor(contentApi, logger) {
			this._logger = logger;
			this._contentAPI = contentApi;
			this._data = new Map();
			this._isDisplayInfoOnDOM = false;
		}

		getId() {
			return this._contentAPI.getId();
		}

		/**
		 * @public
		 * @function getType
		 * @description retrieves the type of the widget and if the widget is static it will transform the string to a user friendly
		 * @return visType the type of the associated widget
		 */
		getType() {
			return this._contentAPI.getType();
		}

		/**
		 * @public
		 * @function getVisTypeLabel
		 * @description retrieves the visualization type label
		 * @return {string} the label for the visualization type
		 */
		getVisTypeLabel() {
			let generalInfo = this.getData(GENERAL_INFO);
			let visLabel = '';
			if (generalInfo.getLabel !== undefined) {
				visLabel = generalInfo.getLabel();
			} else {
				// Remove the "widget." from the string "widget.shape" and capitalize the first letter
				visLabel = this._contentAPI.getType();
				visLabel = visLabel.slice(7, visLabel.length);
				visLabel = visLabel[0].toUpperCase() + visLabel.slice(1);
			}

			return visLabel;
		}

		_getState() {
			return this._contentAPI.getFeature('state');
		}

		setData(key, value) {
			if (typeof key !== 'string') {
				this._logger.error('invalid or undefined key', key);
			} else {
				this._data.set(key, value);
			}
		}

		getData(key) {
			return this._data.get(key);
		}

		getSpec() {
			let error = this._getState().getError();
			let spec = {
				id: this.getId(),
				type: this.getType(),
				visType: this.getVisTypeLabel(),
				state: {
					error: (error) ? error.toJson() : undefined
				}
			};
			this._data.forEach((value, key) => {
				if (typeof value.toJSON === 'function') {
					try {
						spec[key] = value.toJSON();
					} catch (error) {
						this._logger.error('error when adding custom data in spec', key, error);
					}
				} else {
					if(key !== 'generalInfo') {
						spec[key] = value;
					}
				}
			});
			return spec;
		}

		toUIJSON(options = {}) {
			let uiSpec = [
				{
					sectionName: StringResources.get('generalInfo'),
					sectionValues: [{
						fieldName: StringResources.get('widgetId'),
						fieldValue: this.getId()
					}, {
						fieldName: StringResources.get('widgetType'),
						fieldValue: this.getVisTypeLabel()
					}, {
						fieldName: 'linkSpecification',
						fieldValue: StringResources.get('viewSpecification'),
						type: 'Link',
						role: 'button'
					}],
					properties: {
						open: true
					}
				}];

			this._data.forEach((value, key) => {
				if (typeof value.toUIJSON === 'function') {
					try {
						uiSpec = uiSpec.concat(value.toUIJSON(options));
					} catch (error) {
						this._logger.error('error when adding custom UI spec', key, error);
					}
				}
			});

			try {
				uiSpec = this._sanitizeUIJSON(uiSpec);
			} catch (error) {
				this._logger.error('error when adding custom UI spec', error);
			}
			return uiSpec;
		}

		_sanitizeUIJSON(uiSpec){
			uiSpec.forEach((sectionSpec) => {

				if(sectionSpec.sectionName === undefined){
					throw new Error('Error: no section name');
				}

				if(sectionSpec.sectionValues === undefined){
					sectionSpec.sectionValues = [];
				}

				if(sectionSpec.properties === undefined){
					sectionSpec.properties = {
						open: true
					};
				} else if (sectionSpec.properties !== undefined && sectionSpec.properties.open === undefined) {
					sectionSpec.properties.open = true;
				}

				sectionSpec.sectionValues.forEach((fieldSpec) => {
					if(fieldSpec.type === undefined){
						fieldSpec.type = 'Default';
					}
					if(fieldSpec.type === 'SimpleTable'){
						if(!fieldSpec.fieldValue.columnLength){
							throw new Error('Error: no columnLength. Need to specify the column length');
						}
						if(!fieldSpec.fieldValue.rowLength){
							throw new Error('Error: no rowLength. Need to specify the row length');
						}
						if(!fieldSpec.fieldValue.cellValue || typeof(fieldSpec.fieldValue.cellValue) !== 'function'){
							throw new Error('Error: no cellValue or headerValue is not a function');
						}
					}
				});
			});
			return uiSpec;
		}

		isDisplayInfoOnDOM() {
			return this._isDisplayInfoOnDOM;
		}
	}
	return ContentInfoImpl;
});
